/* eslint-disable no-case-declarations */
import { Badge, Text, Image, Input } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';

import { VoucherField, VoucherBrandsField } from 'app/constants/table';

import { DEFAULT_DATE_FORMAT, Voucher, VoucherBrand } from '@aslanpay/portal-common-lib';

// Define EditableBrand type based on fields being edited
type EditableBrand = {
  promoId: string;
  availableToUser: number;
  availableToUserForPaymentGateway: number;
  priority: number;
  promoName?: string;
  promoStartDate?: string;
  promoEndDate?: string;
  // Read-only commission fields for display
  available: number;
  tillo: number;
  providerCommission: number;
  paymentGatewayCommission: number;
  availableToAslan: number;
  availableToAslanPaymentGateway: number;
};

export default function getVoucherColumns() {
  const columnHelper = createColumnHelper<Voucher>();

  // Map each field to a column
  const fields = Object.keys(VoucherField) as Array<keyof typeof VoucherField>;
  const columns = fields.map((field) =>
    columnHelper.accessor(field as keyof Voucher, {
      header: VoucherField[field],
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue();

        switch (field) {
          case 'createdAt':
          case 'expiry':
          case 'externalTransactionDate':
            return value ? format(parseISO(String(value)), DEFAULT_DATE_FORMAT) : '';

          case 'initialBalance':
          case 'currentBalance':
            return `£${value}`;

          case 'paidWithAslan':
            return value ? 'Yes' : 'No';

          case 'status': {
            const statusMap = {
              ACTIVE: 'green',
              REDEEMED: 'blue',
              EXPIRED: 'red',
            } as const;

            return (
              <Badge colorScheme={statusMap[value as keyof typeof statusMap]}>
                {String(value).charAt(0).toUpperCase() + String(value).slice(1).toLowerCase()}
              </Badge>
            );
          }

          default:
            return String(value);
        }
      },
      meta: {},
    })
  );

  return columns;
}

export function getVoucherBrandsColumns({
  isEditing = false,
  onChangeField,
}: {
  isEditing?: boolean;
  onChangeField?: (id: string, key: keyof EditableBrand, value: number) => void;
}) {
  const columnHelper = createColumnHelper<VoucherBrand>();

  return Object.keys(VoucherBrandsField).map((field) =>
    columnHelper.accessor(field as keyof VoucherBrand, {
      header: VoucherBrandsField[field as keyof typeof VoucherBrandsField],
      cell: (info) => {
        const row = info.row.original as VoucherBrand & EditableBrand;
        let value: unknown;

        switch (field) {
          case 'logoUrl':
            value = row.logoUrl;
            return value ? (
              <Image boxSize="40px" src={String(value)} objectFit="contain" alt="Logo" />
            ) : (
              <Text>No logo</Text>
            );

          case 'name':
            return row.name ?? '';

          case 'provider':
            return row.provider ?? '';

          case 'priority':
            if (isEditing) {
              return (
                <Input
                  key={`${row.id}-${field}`}
                  type="number"
                  size="sm"
                  width="80px"
                  value={row.priority?.toString() ?? ''}
                  onChange={(e) => {
                    const parsed = e.target.value === '' ? 0 : Number(e.target.value);
                    onChangeField?.(row.id, field as keyof EditableBrand, parsed);
                  }}
                  inputMode="decimal"
                  min="0"
                />
              );
            }
            return row.priority ?? '';

          case 'id':
            return row.id ?? '';

          case 'merchantId':
            return row.merchantId ?? '';

          case 'promoName':
            value = isEditing
              ? row.promoName ?? row.promotion?.promoName
              : row.promotion?.promoName;
            return value ?? '';

          case 'promoId':
            value = isEditing ? row.promoId ?? row.promotion?.id : row.promotion?.id;
            return value ?? '';

          case 'promoStartDate':
            value = isEditing
              ? row.promoStartDate || row.promotion?.promoStartDate
              : row.promotion?.promoStartDate;
            if (!value) return '';
            const startDate = new Date(value as string);
            return Number.isNaN(startDate.getTime()) ? '' : format(startDate, DEFAULT_DATE_FORMAT);

          case 'promoEndDate':
            value = isEditing
              ? row.promoEndDate || row.promotion?.promoEndDate
              : row.promotion?.promoEndDate;
            if (!value) return '';
            const endDate = new Date(value as string);
            return Number.isNaN(endDate.getTime()) ? '' : format(endDate, DEFAULT_DATE_FORMAT);

          case 'availableToUser':
            if (isEditing) {
              return (
                <Input
                  key={`${row.promoId}-${field}`}
                  type="text"
                  size="sm"
                  width="80px"
                  defaultValue={row.availableToUser ?? ''}
                  onBlur={({ target: { value: inputValue } }) => {
                    // Only update the data model when leaving the field
                    if (inputValue === '') {
                      onChangeField?.(row.promoId, field as keyof EditableBrand, 0);
                    } else {
                      const num = Number(inputValue);
                      if (!Number.isNaN(num) && num >= 0) {
                        onChangeField?.(row.promoId, field as keyof EditableBrand, num);
                      }
                    }
                  }}
                  inputMode="decimal"
                />
              );
            }
            value = row.promotion?.commission?.availableToUser;
            return value ?? '';

          case 'availableToUserForPaymentGateway':
            if (isEditing) {
              return (
                <Input
                  key={`${row.promoId}-${field}`}
                  type="text"
                  size="sm"
                  width="80px"
                  defaultValue={row.availableToUserForPaymentGateway ?? ''}
                  onBlur={({ target: { value: inputValue } }) => {
                    // Only update the data model when leaving the field
                    if (inputValue === '') {
                      onChangeField?.(row.promoId, field as keyof EditableBrand, 0);
                    } else {
                      const num = Number(inputValue);
                      if (!Number.isNaN(num) && num >= 0) {
                        onChangeField?.(row.promoId, field as keyof EditableBrand, num);
                      }
                    }
                  }}
                  inputMode="decimal"
                />
              );
            }
            value = row.promotion?.commission?.availableToUserPaymentGateway;
            return value ?? '';

          case 'available':
            value = isEditing ? row.available : row.promotion?.commission?.available;
            return (
              <Text
                color={isEditing ? 'gray.500' : 'inherit'}
                fontStyle={isEditing ? 'italic' : 'normal'}
                fontSize="sm"
              >
                {String(value ?? '')}
              </Text>
            );

          case 'tillo':
            value = isEditing ? row.tillo : row.promotion?.commission?.tillo;
            return (
              <Text
                color={isEditing ? 'gray.500' : 'inherit'}
                fontStyle={isEditing ? 'italic' : 'normal'}
                fontSize="sm"
              >
                {String(value ?? '')}
              </Text>
            );

          case 'providerCommission':
            value = isEditing ? row.providerCommission : row.promotion?.commission?.provider;
            return (
              <Text
                color={isEditing ? 'gray.500' : 'inherit'}
                fontStyle={isEditing ? 'italic' : 'normal'}
                fontSize="sm"
              >
                {String(value ?? '')}
              </Text>
            );

          case 'paymentGatewayCommission':
            value = isEditing
              ? row.paymentGatewayCommission
              : row.promotion?.commission?.paymentGateway;
            return (
              <Text
                color={isEditing ? 'gray.500' : 'inherit'}
                fontStyle={isEditing ? 'italic' : 'normal'}
                fontSize="sm"
              >
                {String(value ?? '')}
              </Text>
            );

          case 'availableToAslan':
            value = isEditing ? row.availableToAslan : row.promotion?.commission?.availableToAslan;
            return (
              <Text
                color={isEditing ? 'gray.500' : 'inherit'}
                fontStyle={isEditing ? 'italic' : 'normal'}
                fontSize="sm"
              >
                {String(value ?? '')}
              </Text>
            );

          case 'availableToAslanPaymentGateway':
            value = isEditing
              ? row.availableToAslanPaymentGateway
              : row.promotion?.commission?.availableToAslanPaymentGateway;
            return (
              <Text
                color={isEditing ? 'gray.500' : 'inherit'}
                fontStyle={isEditing ? 'italic' : 'normal'}
                fontSize="sm"
              >
                {String(value ?? '')}
              </Text>
            );

          default:
            return '';
        }
      },
      enableSorting: false,
      meta: field === 'logoUrl' ? { customStyles: { justifyContent: 'center' } } : undefined,
    })
  );
}
